import { array } from 'fp-ts';
import { pipe } from 'fp-ts/function';

import { TimeUnitPeriod } from '@mews-distributor/data-access';
import { values } from '@mews/fp';

import { DateTimeUtcService } from '@mews/framework';
import { getReservations } from '../../../modules/entities/reservations/selectors';
import { getSelectedHotelRates } from '../../../modules/hotels/selectors';
import {
    getCurrentReservationTotalPriceWithoutUserProducts,
    getCurrentReservationUnitPriceWithoutUserProducts,
} from '../../../modules/productOrders/selectors';
import { getCategories } from '../../../modules/resourceCategories/selectors';
import { ApplicationState } from '../../../types';
import { ViewResourceOrder } from './types';

export const getViewResourceOrders = (
    state: ApplicationState,
    { shouldSelectedCurrencyBeUsed }: { shouldSelectedCurrencyBeUsed: boolean }
): ViewResourceOrder[] => {
    const reservations = getReservations(state);
    const categories = getCategories(state);
    const rates = getSelectedHotelRates(state);

    return pipe(
        reservations,
        values,
        array.map((reservation) => {
            const { roomId, rateId } = reservation;

            const room = categories[roomId];
            const rate = rates[rateId];

            const unitPrice = getCurrentReservationUnitPriceWithoutUserProducts(state, {
                reservationId: reservation.orderId,
                shouldSelectedCurrencyBeUsed,
            });
            const totalPrice = getCurrentReservationTotalPriceWithoutUserProducts(state, {
                reservationId: reservation.orderId,
                shouldSelectedCurrencyBeUsed,
            });

            const promotedServices = reservation.promotedServices.map((promotedService) => {
                const { timeUnitPeriod, startUtc, endUtc } = promotedService;

                const hourCount =
                    timeUnitPeriod === TimeUnitPeriod.Hour && startUtc != null && endUtc != null
                        ? DateTimeUtcService.diffHours(
                              DateTimeUtcService.parse(endUtc),
                              DateTimeUtcService.parse(startUtc)
                          )
                        : undefined;

                return {
                    ...promotedService,
                    hourCount,
                    unitPrice: getCurrentReservationUnitPriceWithoutUserProducts(state, {
                        reservationId: promotedService.reservationId,
                        shouldSelectedCurrencyBeUsed,
                    }),
                    totalPrice: getCurrentReservationTotalPriceWithoutUserProducts(state, {
                        reservationId: promotedService.reservationId,
                        shouldSelectedCurrencyBeUsed,
                    }),
                };
            });

            return {
                reservation: { ...reservation, promotedServices },
                unitPrice,
                totalPrice,
                room,
                rate,
            };
        })
    );
};
